import { db, exists } from '@/firebaseApp'

export type RestaurantPublic = {
  name: string
  backgroundImageUrl: string
  logoSmallUrl: string
  logoBigUrl: string
  seatingWelcomeCopy?: string | null
  thankyouTitle?: string | null
  thankyouCopy?: string | null
  thankyouTipCopy?: string | null
}

export type Role = {
  [uid: string]: boolean
}

export type RestaurantIndex = {
  [uid: string]: boolean
}

export type Restaurant = {
  public: RestaurantPublic,
  owners: Role[],
  staff: Role[]
}

export type RestaurantList = {
  [index: string]: Restaurant
}

class Restaurants {
  async getAllRestaurants(f: (restaurants: RestaurantList) => void) {
    return db
      .ref(`restaurants`)
      .orderByChild('name')
      .on('value', s => f(s.val()))
  }

  async getRestaurant(restaurantId: string): Promise<Restaurant> {
    return db.ref(`restaurants/${restaurantId}`).once('value').then(o => o.val())
  }

  async getAllPublicRestaurants(): Promise<RestaurantIndex[]> {
    return db.ref(`restaurantList`).once('value').then(o => o.val())
  }

  async getPublicRestaurant(restaurantId: string): Promise<RestaurantPublic> {
    return db.ref(`restaurants/${restaurantId}/public`).once('value').then(o => o.val())
  }

  async getAllStaff(restaurantId: string, f: (restaurants: Role[]) => void) {
    return db
      .ref(`restaurants/${restaurantId}/staff`)
      .on('value', s => f(s.val()))
  }

  async create(restaurantId: string, name: string) {
    const restaurantRef = db.ref(`restaurants/${restaurantId}`)
    if (await exists(restaurantRef)) {
      return false
    }

    // Create the restaurant
    await restaurantRef.set({
      public: {
        name,
      }
    })

    return true
  }

  async publishRestaurant(restaurantId: string) {
    // Add it to the public restaurant list too
    return db.ref(`restaurantList/${restaurantId}`).set(true)
  }

  async unpublishRestaurant(restaurantId: string) {
    // Add it to the public restaurant list too
    return db.ref(`restaurantList/${restaurantId}`).remove()
  }

  nameToId(name: string) {
    return name.replace(/\s/g, '-').replace(/[^a-zA-Z0-9-]/g, '').toLowerCase()
  }

  async deleteRestaurant(restaurantId: string) {
    await db.ref(`restaurants/${restaurantId}`).remove()
    // Remove it from the public restaurant list too
    await db.ref(`restaurantList/${restaurantId}`).remove()
  }

  async updateRestaurant(restaurantId: string, restaurantObject: RestaurantPublic) {
    const restaurantRef = db.ref(`restaurants/${restaurantId}/public`)
    if (!await exists(restaurantRef)) {
      return false
    }

    try {
      await restaurantRef.update(restaurantObject)
    } catch(err) {
      console.error('updateRestaurant', err);
      return false;
    }

    return true
  }

  async deleteOwner(restaurantId: string, uid: string) {
    await db.ref(`restaurants/${restaurantId}/owners/${uid}`).remove()
  }

  async deleteStaff(restaurantId: string, uid: string) {
    await db.ref(`restaurants/${restaurantId}/staff/${uid}`).remove()
  }


}

export default new Restaurants()
